import React, { useCallback, useEffect, useState } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)") — a korábbi FIX
// szövegű ágak eldobták a szerver indoklását. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../utils/apiErrors';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
} from '@mui/material';
import { useResponsive } from '../hooks/useResponsive';
import {
    messageService,
    SentMessageListItem,
    SentMessageDetail,
} from '../services/messageService';
import { formatServerDateTime } from '../utils/dateUtils';

const PAGE_SIZE = 25;

// [Web UX 1. csomag (4.3), 2026-10-03] „Z”-vel érkező üzenet-időbélyeg (`MessagesController.cs:268, :447, :462`) — a
// közös segéd változatlanul értelmezi, ugyanazzal az alakkal (`2026. 10. 02. 15:15`).
function formatDate(iso?: string | null): string {
    return formatServerDateTime(iso);
}

/**
 * Elküldött üzenetek az adminnak.
 *
 * Eddig a kiküldött üzenetnek nem volt semmilyen nyoma a felületen: az admin nem tudta
 * megnézni, mit küldött ki, kinek ment, és elolvasták-e. A címzett- és olvasottság-számok
 * azért megbízhatóak, mert a címzett saját törlése csak jelölés — a példány-sort nem veszi ki.
 */
const AdminSentMessages: React.FC = () => {
    const { isMobile } = useResponsive();
    const [messages, setMessages] = useState<SentMessageListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    const [detail, setDetail] = useState<SentMessageDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');

    const load = useCallback(async (offset: number) => {
        setLoading(true);
        setError('');
        try {
            const data = await messageService.getSentMessages(offset, PAGE_SIZE);
            setMessages(data);
            // Ha teli oldalt kaptunk, valószínűleg van még — a végpont nem ad összesített darabszámot.
            setHasMore(data.length === PAGE_SIZE);
            setSkip(offset);
        } catch (err) {
            setError(apiErrorMessage(err, 'Nem sikerült betölteni az elküldött üzeneteket.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(0);
    }, [load]);

    const openDetail = async (messageId: string) => {
        setDetailLoading(true);
        setDetailError('');
        setDetail(null);
        try {
            setDetail(await messageService.getSentMessage(messageId));
        } catch (err) {
            setDetailError(apiErrorMessage(err, 'Nem sikerült betölteni az üzenetet.'));
        } finally {
            setDetailLoading(false);
        }
    };

    if (loading && messages.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {messages.length === 0 && !error ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    Még nem küldtél ki üzenetet.
                </Typography>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Tárgy</TableCell>
                                {/* Telefonon a küldő és az időpont kimarad: hat oszlop vízszintes
                                    görgetésbe kényszerítené a táblát, és a lényeg (kinek ment,
                                    hányan olvasták) kiszorulna a képernyőről. Az adatok a
                                    Részletek ablakban ott vannak. */}
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Küldő</TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Időpont</TableCell>
                                <TableCell align="center">Címzett</TableCell>
                                <TableCell align="center">Olvasta</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {messages.map((m) => (
                                <TableRow key={m.messageId} hover>
                                    <TableCell sx={{ maxWidth: { xs: 160, sm: 320 } }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>{m.subject}</Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                                            {m.preview}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{m.senderName}</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{formatDate(m.sentDate)}</TableCell>
                                    <TableCell align="center">{m.recipientCount}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            size="small"
                                            label={`${m.readCount} / ${m.recipientCount}`}
                                            color={m.readCount === m.recipientCount && m.recipientCount > 0 ? 'success' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={() => openDetail(m.messageId)}>Részletek</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {(skip > 0 || hasMore) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button disabled={skip === 0 || loading} onClick={() => load(Math.max(0, skip - PAGE_SIZE))}>
                        Előző
                    </Button>
                    <Button disabled={!hasMore || loading} onClick={() => load(skip + PAGE_SIZE)}>
                        Következő
                    </Button>
                </Box>
            )}

            <Dialog open={Boolean(detail) || detailLoading || Boolean(detailError)} onClose={() => { setDetail(null); setDetailError(''); }} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle>{detail?.subject || 'Üzenet'}</DialogTitle>
                <DialogContent dividers>
                    {detailLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
                    {detailError && <Alert severity="error">{detailError}</Alert>}
                    {detail && (
                        <>
                            <Typography variant="caption" color="text.disabled">
                                {detail.senderName} · {formatDate(detail.sentDate)}
                                {detail.eventName ? ` · Esemény: ${detail.eventName}` : ''}
                            </Typography>
                            <Typography sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>{detail.body}</Typography>

                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" gutterBottom>
                                Címzettek ({detail.readCount} / {detail.recipientCount} olvasta)
                            </Typography>
                            <Table size="small">
                                <TableBody>
                                    {detail.recipients.map((r) => (
                                        <TableRow key={r.userId}>
                                            <TableCell>{r.name}</TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {r.isRead
                                                    ? <Chip size="small" color="success" label={`Olvasta · ${formatDate(r.readDate)}`} />
                                                    : <Chip size="small" label="Olvasatlan" />}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDetail(null); setDetailError(''); }}>Bezárás</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminSentMessages;
